import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { createPortal } from "react-dom";

import { Icon, type IconName } from "./Icon";

/** Number input that only commits on blur or Enter, so typing "150" works. */
function NumberField({
  label,
  value,
  min,
  max,
  unit,
  disabled,
  onCommit,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  unit: string;
  disabled?: boolean;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const parsed = Number(draft);
    setDraft(null);
    if (draft.trim() !== "" && Number.isFinite(parsed)) {
      onCommit(Math.max(min, Math.min(max, Math.round(parsed))));
    }
  };
  return (
    <span className="number-field">
      <input
        type="number"
        inputMode="numeric"
        aria-label={`${label}の値`}
        value={draft ?? value}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
          if (event.key === "Escape") setDraft(null);
        }}
      />
      <span>{unit}</span>
    </span>
  );
}

interface RangeControlProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  disabled?: boolean;
  onChange: (value: number) => void;
  onInteractionStart?: () => void;
  onInteractionEnd?: () => void;
}

export function RangeControl({
  label,
  value,
  min,
  max,
  step,
  unit,
  disabled,
  onChange,
  onInteractionStart,
  onInteractionEnd,
}: RangeControlProps) {
  const interactionActiveRef = useRef(false);
  const onInteractionEndRef = useRef(onInteractionEnd);
  const startInteraction = () => {
    if (interactionActiveRef.current) return;
    interactionActiveRef.current = true;
    onInteractionStart?.();
  };
  const endInteraction = () => {
    if (!interactionActiveRef.current) return;
    interactionActiveRef.current = false;
    onInteractionEnd?.();
  };
  const isAdjustmentKey = (key: string) =>
    [
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "End",
      "Home",
      "PageDown",
      "PageUp",
    ].includes(key);

  useEffect(() => {
    onInteractionEndRef.current = onInteractionEnd;
  }, [onInteractionEnd]);

  useEffect(() => {
    const handleWindowBlur = () => {
      if (!interactionActiveRef.current) return;
      interactionActiveRef.current = false;
      onInteractionEndRef.current?.();
    };
    window.addEventListener("blur", handleWindowBlur);
    return () => {
      window.removeEventListener("blur", handleWindowBlur);
      handleWindowBlur();
    };
  }, []);

  return (
    <div className="range-control">
      <span className="control-label">{label}</span>
      <NumberField
        label={label}
        value={value}
        min={min}
        max={max}
        unit={unit}
        disabled={disabled}
        onCommit={(next) => {
          onInteractionStart?.();
          onChange(next);
          onInteractionEnd?.();
        }}
      />
      <input
        className="range-input"
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        aria-label={label}
        aria-valuetext={`${value}${unit}`}
        onPointerDown={(event) => {
          startInteraction();
          try {
            event.currentTarget.setPointerCapture(event.pointerId);
          } catch {
            // Pointer capture may be unavailable for synthetic events.
          }
        }}
        onPointerUp={endInteraction}
        onPointerCancel={endInteraction}
        onLostPointerCapture={endInteraction}
        onKeyDown={(event) => {
          if (isAdjustmentKey(event.key)) startInteraction();
        }}
        onKeyUp={(event) => {
          if (isAdjustmentKey(event.key)) endInteraction();
        }}
        onBlur={endInteraction}
        onInput={(event) => {
          startInteraction();
          onChange(Number(event.currentTarget.value));
        }}
      />
    </div>
  );
}

export function Switch({
  checked,
  label,
  icon,
  disabled,
  onChange,
}: {
  checked: boolean;
  label: string;
  icon?: IconName;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="switch-row">
      <span>
        {icon && <Icon name={icon} size={16} />}
        {label}
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="switch-track" aria-hidden="true">
        <span />
      </span>
    </label>
  );
}

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  icon?: IconName;
  title?: string;
}

export function Segmented<T extends string>({
  value,
  options,
  label,
  disabled,
  block,
  onChange,
}: {
  value: T;
  options: ReadonlyArray<SegmentOption<T>>;
  label: string;
  disabled?: boolean;
  block?: boolean;
  onChange: (value: T) => void;
}) {
  return (
    <div
      className={`segmented ${block ? "is-block" : ""}`}
      role="group"
      aria-label={label}
    >
      {options.map((option) => (
        <button
          key={option.value}
          className={`segment ${value === option.value ? "is-active" : ""} ${
            option.icon ? "has-icon" : ""
          }`}
          type="button"
          aria-pressed={value === option.value}
          aria-label={option.icon ? option.label : undefined}
          title={option.title ?? (option.icon ? option.label : undefined)}
          disabled={disabled}
          onClick={() => onChange(option.value)}
        >
          {option.icon ? <Icon name={option.icon} size={18} /> : option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A trigger button with a floating panel, like Canva's toolbar dropdowns.
 * The panel is portalled to the body so a scrolling or transformed toolbar
 * cannot clip or offset it.
 */
export function Popover({
  label,
  trigger,
  disabled,
  className = "",
  children,
}: {
  label: string;
  trigger: ReactNode;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const id = useId();

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const trigger = triggerRef.current?.getBoundingClientRect();
      const panel = panelRef.current?.getBoundingClientRect();
      if (!trigger) return;
      const width = panel?.width ?? 240;
      setPosition({
        top: trigger.bottom + 8,
        left: Math.max(8, Math.min(window.innerWidth - width - 8, trigger.left)),
      });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!panelRef.current?.contains(target) && !triggerRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    window.addEventListener("pointerdown", close);
    window.addEventListener("keydown", escape, true);
    return () => {
      window.removeEventListener("pointerdown", close);
      window.removeEventListener("keydown", escape, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        className={`popover-trigger ${open ? "is-open" : ""} ${className}`}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        title={label}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        {trigger}
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={id}
            className="popover-panel"
            role="dialog"
            aria-label={label}
            style={position}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}

/** Toolbar button that shows a value and opens a slider for it. */
export function SliderPopover({
  label,
  shortLabel,
  value,
  mixed,
  ...range
}: Omit<Parameters<typeof RangeControl>[0], "label"> & {
  label: string;
  shortLabel: string;
  mixed?: boolean;
}) {
  return (
    <Popover
      label={label}
      disabled={range.disabled}
      trigger={
        <>
          <span className="popover-trigger-label">{shortLabel}</span>
          <span className="popover-trigger-value">
            {mixed ? "—" : `${value}${range.unit}`}
          </span>
          <Icon name="chevron-down" size={14} />
        </>
      }
    >
      <RangeControl label={label} value={value} {...range} />
    </Popover>
  );
}

export function ColorPicker({
  value,
  colors,
  label,
  disabled,
  onChange,
}: {
  value: string;
  colors: readonly string[];
  label: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const custom = !colors.includes(value);
  return (
    <div className="swatch-row" role="group" aria-label={label}>
      {colors.map((color) => (
        <button
          key={color}
          className={`swatch ${value === color ? "is-active" : ""}`}
          type="button"
          style={{ background: color }}
          aria-label={color}
          aria-pressed={value === color}
          disabled={disabled}
          onClick={() => onChange(color)}
        />
      ))}
      <label
        className={`swatch swatch-custom ${custom ? "is-active" : ""}`}
        title="ほかの色"
        style={custom ? { background: value } : undefined}
      >
        <input
          type="color"
          value={value}
          aria-label="ほかの色を選ぶ"
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      </label>
    </div>
  );
}
